import { finalize, forkJoin, switchMap, take } from "rxjs";
import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormsModule } from "@angular/forms";
import { SelectModule } from "primeng/select";
import { ClientGroupService } from "../../../../../core/services/client-group.service";
import { ProjectAccessListDto } from "../../../../../core/models/client-group/project-access-list.dto";
import { DialogModule } from "primeng/dialog";
import { ButtonModule } from "primeng/button";
import { InputTextModule } from "primeng/inputtext";
import { EditorModule } from "primeng/editor";
import * as MicrosoftGraph from "@microsoft/microsoft-graph-types";
import {
  DroppedFile,
  GlobalFileDragDropDirective,
} from "../../../../../shared/directives/global-file-drag-drop.directive";
import { MsalApiService } from "../../../../../core/services/msal-api.service";
import { CommonService } from "../../../../../core/services/common.service";
import { ProjectWorkflowService } from "../../../../../core/services/project-workflow.service";
import { JniToastrService } from "../../../../../core/services/jni-toastr.service";
import { LoaderService } from "../../../../../core/services/loader.service";
import { ChatGptRequestDto } from "../../../../../core/models/common/chatGptRequestDto";
import { WorkflowUpdatesDto } from "../../../../../core/models/project-workflow/workflowUpdatesDto";
import { getBase64 } from "../../../../../core/services/shared.service";
import { ProductionWorkflowLineDto } from "../../../../../core/models/project-workflow/productionWorkflowLineDto";
import { generateClientPortalEmailHtml } from "./email-template";
import {
  NADEL_WORDMARK_SVG_BASE64,
  NADEL_BANNER_SVG_BASE64,
  NADEL_LOGO_SVG_BASE64,
} from "./email-images";

export interface EmailRecipient {
  email: string;
  displayName: string;
  recipientType: string;
  addType: string;
}

export interface EmailAttachment {
  fileName: string;
  fileSize: string;
  /** Raw byte size of the attachment, used for collective size enforcement. */
  fileSizeBytes?: number;
  /** Base64-encoded file content (without a data-URI prefix) used to send the attachment. */
  base64?: string;
  /** MIME type of the attachment. */
  contentType?: string;
  /** Indicates the attachment is the mandatory, non-removable consolidated proof PDF. */
  isDefault?: boolean;
}

@Component({
  selector: "jni-client-portal-email-modal",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    SelectModule,
    DialogModule,
    ButtonModule,
    InputTextModule,
    EditorModule,
    GlobalFileDragDropDirective,
  ],
  templateUrl: "./client-portal-email-modal.component.html",
  styleUrls: ["./client-portal-email-modal.component.scss"],
})
export class ClientPortalEmailModalComponent implements OnChanges {
  @Input() visible = false;

  /** Identifier of the current project (workflow), used when recording the email activity. */
  @Input() workflowId = 0;

  /** Name of the current project, available for message composition context. */
  @Input() projectName = "";

  /** Recipients pre-populated from the project access buyer and additional users. */
  @Input() initialRecipients: EmailRecipient[] = [];

  /** Default, non-removable consolidated proof PDF attachment. */
  @Input() defaultAttachment: EmailAttachment | null = null;

  /** Customer / company name shown in the subject and email template. */
  @Input() clientName = "";

  /** D365 sales order number used as the order reference in the subject and email template. */
  @Input() orderNumber = "";

  /** Direct link to the client-portal project page embedded in the email template. */
  @Input() clientPortalLink = "";

  /** Order lines of the current project, rendered in the email template body. */
  @Input() orderLines: ProductionWorkflowLineDto[] = [];

  /**
   * Explicit display name of the primary buyer to use in the "Hi [name]," email greeting.
   * Takes precedence over the recipient list lookup.
   */
  @Input() buyerDisplayName = "";

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() emailSent = new EventEmitter<void>();

  emailSubject = "";
  /** Rich-text HTML typed by the user in the email body editor. Empty means the default template copy is used. */
  bodyText = "";

  recipients: EmailRecipient[] = [];

  attachments: EmailAttachment[] = [];

  private readonly clientGroupService = inject(ClientGroupService);
  private readonly destroyRef = inject(DestroyRef);
  availableRecipients: EmailRecipient[] = [];
  loadingRecipients = false;
  isAddingNewRecipient = false;
  newRecipient: EmailRecipient = {
    email: "",
    displayName: "",
    recipientType: "-",
    addType: "TO",
  };
  selectedRecipientIndex: number | null = null;

  /** Available address headers; every recipient must have active project access. */
  readonly recipientTypeOptions = ["TO", "CC", "BCC"];

  // ChatGPT assistant state
  displayChatGptModal = false;
  chatGptPrompt = "";
  showGptLoader = false;

  isSending = false;

  constructor(
    private _msalApiService: MsalApiService,
    private _commonService: CommonService,
    private _projectWorkflowService: ProjectWorkflowService,
    private _toastr: JniToastrService,
    private _loaderService: LoaderService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["initialRecipients"]) {
      this.recipients = (this.initialRecipients ?? []).map(recipient => ({
        ...recipient,
      }));
    }

    if (changes["defaultAttachment"]) {
      const MAX_SIZE = 10 * 1024 * 1024; // 10 MB
      if ((this.defaultAttachment?.fileSizeBytes ?? 0) > MAX_SIZE) {
        this._toastr.error("Error", "File size exceed 10 MB");
      }
      this.resetAttachments();
    }

    // Reset the composition state whenever the modal is (re)opened.
    if (changes["visible"] && this.visible) {
      this.selectedRecipientIndex = null;
      this.isAddingNewRecipient = false;
      this.resetAttachments();
      this.emailSubject = this.buildDefaultSubject();
      this.bodyText = "";
      this.recipients = (this.initialRecipients ?? []).map(r => ({ ...r }));
    }
  }

  /** Restores the attachment list to contain only the default consolidated proof PDF. */
  private resetAttachments(): void {
    this.attachments = this.defaultAttachment ? [this.defaultAttachment] : [];
  }

  closeModal() {
    if (this.isSending) return;
    this.selectedRecipientIndex = null;
    this.isAddingNewRecipient = false;
    this.resetAttachments();
    this.emailSubject = "";
    this.bodyText = "";
    this.recipients = (this.initialRecipients ?? []).map(r => ({ ...r }));
    this.visibleChange.emit(false);
  }

  /** Builds the default email subject from client name, project name, and order number. */
  private buildDefaultSubject(): string {
    return ["FOR APPROVAL", this.clientName, this.projectName, this.orderNumber]
      .filter(part => !!part?.trim())
      .join(" - ");
  }

  /** Generates the final branded HTML email. */
  private buildFinalEmailHtml(): string {
    return generateClientPortalEmailHtml({
      orderNumber: this.orderNumber,
      clientPortalLink: this.clientPortalLink,
      clientName: this.clientName,
      orderLines: this.orderLines,
      customBodyHtml: this.bodyText?.trim() || undefined,
    });
  }

  addRecipient() {
    if (this.isSending || this.loadingRecipients || !this.workflowId) return;
    this.isAddingNewRecipient = true;
    this.availableRecipients = [];
    this.newRecipient = {
      email: "",
      displayName: "",
      recipientType: "-",
      addType: "TO",
    };
    this.selectedRecipientIndex = null;
    const projectId = this.workflowId;
    this.loadingRecipients = true;
    this.clientGroupService
      .getProjectAccess(projectId)
      .pipe(
        take(1),
        finalize(() => (this.loadingRecipients = false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: access => {
          if (projectId === this.workflowId)
            this.availableRecipients = this.activeRecipients(access);
        },
        error: () =>
          this._toastr.error(
            "Unable to load recipients",
            "Current project access could not be verified. Please try again."
          ),
      });
  }

  confirmNewRecipient() {
    if (this.isSending || this.loadingRecipients) return;
    const active = this.availableRecipients.find(
      recipient =>
        recipient.email.toLowerCase() ===
        this.newRecipient.email.trim().toLowerCase()
    );
    if (
      !active ||
      !this.recipientTypeOptions.includes(this.newRecipient.addType)
    ) {
      this._toastr.error(
        "Invalid recipient",
        "Select a recipient with active project access. Held or removed recipients cannot be emailed."
      );
      return;
    }
    this.recipients.push({ ...active, addType: this.newRecipient.addType });
    this.isAddingNewRecipient = false;
  }

  /** Uses only active individual/group access, never the held approval list or client directory. */
  private activeRecipients(access: ProjectAccessListDto): EmailRecipient[] {
    const individuals = access.individualUsers.map(user => ({
      email: user.email.trim(),
      displayName: user.displayName,
      recipientType: user.isPrimaryBuyer ? "Buyer" : "Additional",
      addType: "TO",
    }));
    const members = access.groups.flatMap(group =>
      group.users.map(user => ({
        email: user.email.trim(),
        displayName: user.displayName,
        recipientType: group.groupName,
        addType: "TO",
      }))
    );
    return [
      ...new Map(
        [...individuals, ...members].map(recipient => [
          recipient.email.toLowerCase(),
          recipient,
        ])
      ).values(),
    ];
  }

  cancelNewRecipient() {
    this.isAddingNewRecipient = false;
  }

  selectRecipient(index: number) {
    this.selectedRecipientIndex =
      this.selectedRecipientIndex === index ? null : index;
  }

  removeRecipient(index: number) {
    if (this.isSending) return;
    if (index >= 0 && index < this.recipients.length) {
      this.recipients.splice(index, 1);
      this.selectedRecipientIndex = null;
    }
  }

  onFilesDropped(files: DroppedFile[]) {
    const MAX_TOTAL_SIZE = 10 * 1024 * 1024; // 10 MB collective limit
    files.forEach(dropped => {
      const file = dropped.file;
      const currentTotal = this.attachments.reduce((sum, a) => {
        if (a.fileSizeBytes !== undefined) return sum + a.fileSizeBytes;
        return sum + (a.base64 ? Math.ceil((a.base64.length * 3) / 4) : 0);
      }, 0);
      if (currentTotal + file.size > MAX_TOTAL_SIZE) {
        this._toastr.error(
          "Error",
          `Cannot attach ${file.name}: total attachment size would exceed the 10 MB limit.`
        );
        return;
      }
      getBase64(file)
        .then(result => {
          const base64 =
            typeof result === "string" ? (result.split(",")[1] ?? "") : "";
          this.attachments.push({
            fileName: file.name,
            fileSize: `${Math.round(file.size / 1024)}kb size`,
            fileSizeBytes: file.size,
            base64,
            contentType: file.type || "application/octet-stream",
            isDefault: false,
          });
        })
        .catch(() => {
          this._toastr.error(
            "Error",
            `Unable to attach file ${file.name}. Please try again.`
          );
        });
    });
  }

  removeAttachment(index: number) {
    // The default consolidated proof PDF cannot be removed.
    if (this.attachments[index]?.isDefault) {
      return;
    }
    this.attachments.splice(index, 1);
  }

  /** Opens the ChatGPT assistant popup. */
  onChatGptClick() {
    this.displayChatGptModal = true;
  }

  /** Closes the ChatGPT assistant popup without generating content. */
  cancelChatGpt() {
    this.displayChatGptModal = false;
    this.chatGptPrompt = "";
  }

  /**
   * Sends the entered prompt to ChatGPT and inserts the generated response into the email body,
   * then closes the assistant popup.
   */
  submitChatGptPrompt() {
    const prompt = this.chatGptPrompt.trim();
    if (!prompt) {
      this._toastr.error("Error", "Prompt cannot be empty");
      return;
    }

    this.showGptLoader = true;
    const payload = {
      messages: [{ role: "user", content: prompt }],
    } as ChatGptRequestDto;

    this._commonService.postChatGPTRequest(payload).subscribe({
      next: res => {
        if (res?.data) {
          this.bodyText = `${this.bodyText ?? ""}${res.data}`;
        }
        this.chatGptPrompt = "";
        this.displayChatGptModal = false;
        this.showGptLoader = false;
      },
      error: () => {
        this._toastr.error("Error", "Unable to get data from Chat GPT");
        this.showGptLoader = false;
      },
    });
  }

  /**
   * Sends the composed email via the logged-in user's mailbox using Microsoft Graph and records
   * the email as an activity in the project activity feed.
   * One personalised email is sent per TO recipient so that the "Hi [name]" greeting is correct
   * for each individual. CC recipients receive a copy of every send.
   */
  sendEmail() {
    if (this.isSending) {
      return;
    }

    if (!this.workflowId) {
      this._toastr.error("Error", "Select a project before sending email.");
      return;
    }
    const projectId = this.workflowId;
    // Snapshot exactly what will be sent so edits during validation cannot introduce unchecked recipients.
    const recipients = this.recipients.map(recipient => ({
      ...recipient,
      email: recipient.email.trim(),
    }));
    const toRecipientsList = recipients.filter(
      r => r.addType?.toUpperCase() === "TO"
    );
    const toRecipients = this.buildGraphRecipients("TO", recipients);
    const ccRecipients = this.buildGraphRecipients("CC", recipients);
    const bccRecipients = this.buildGraphRecipients("BCC", recipients);

    if (toRecipientsList.length === 0) {
      this._toastr.error("Error", "Add at least one recipient.");
      return;
    }

    const attachments: MicrosoftGraph.Attachment[] = [
      ...this.buildGraphAttachments(),
      ...this.buildSvgCidAttachments(),
    ];

    const mails = toRecipientsList.map(toRecipient => ({
      message: {
        subject: this.emailSubject || "",
        body: {
          contentType: "html",
          content: this.buildFinalEmailHtml(),
        },
        toRecipients: [
          {
            emailAddress: {
              address: toRecipient.email,
              name: toRecipient.displayName || undefined,
            },
          },
        ],
        ccRecipients,
        bccRecipients,
        attachments,
      } as MicrosoftGraph.Message,
    }));

    this.isSending = true;
    this._loaderService.setLoaderState(true);
    // Fresh access is required for every send, including approved requests still awaiting dispatch.
    this.clientGroupService
      .getProjectAccess(projectId)
      .pipe(
        take(1),
        switchMap(access => {
          if (
            projectId !== this.workflowId ||
            String(access.projectId) !== String(projectId)
          ) {
            throw new Error(
              "The project changed. Reopen the email composer before sending."
            );
          }
          const active = new Set(
            this.activeRecipients(access).map(recipient =>
              recipient.email.toLowerCase()
            )
          );
          const blocked = recipients.filter(
            recipient =>
              !active.has(recipient.email.toLowerCase()) ||
              !this.recipientTypeOptions.includes(
                recipient.addType?.toUpperCase()
              )
          );
          if (blocked.length) {
            throw new Error(
              `Email not sent. These recipients do not have active project access or have an invalid address type: ${blocked.map(recipient => recipient.email).join(", ")}. Remove them or wait for approval and invitation dispatch.`
            );
          }
          return forkJoin(
            mails.map(mail => this._msalApiService.sendMail(mail))
          );
        }),
        finalize(() => {
          this._loaderService.setLoaderState(false);
          this.isSending = false;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          this.isSending = false;
          this._toastr.success("Success", "Email sent successfully");
          this.recordEmailActivity(toRecipients, ccRecipients);
          this.emailSent.emit();
          this.closeModal();
        },
        error: (error: unknown) => {
          this._toastr.error(
            "Email not sent",
            error instanceof Error
              ? error.message
              : "Unable to verify project access or send email. Please try again."
          );
        },
      });
  }

  /** Maps the recipients of the given add type into Microsoft Graph recipient objects. */
  private buildGraphRecipients(
    addType: string,
    recipients: EmailRecipient[]
  ): MicrosoftGraph.Recipient[] {
    return recipients
      .filter(recipient => recipient.addType?.toUpperCase() === addType)
      .map(recipient => ({
        emailAddress: {
          address: recipient.email,
          name: recipient.displayName || undefined,
        },
      }));
  }

  /** Builds three CID inline SVG attachments for the email header, banner, and footer images. */
  private buildSvgCidAttachments(): MicrosoftGraph.Attachment[] {
    const prefix = "data:image/svg+xml;base64,";
    const strip = (dataUri: string) =>
      dataUri.startsWith(prefix) ? dataUri.substring(prefix.length) : dataUri;

    return [
      {
        "@odata.type": "#microsoft.graph.fileAttachment",
        name: "nadel-wordmark.svg",
        contentType: "image/svg+xml",
        contentBytes: strip(NADEL_WORDMARK_SVG_BASE64),
        isInline: true,
        contentId: "nadelWordmark",
      },
      {
        "@odata.type": "#microsoft.graph.fileAttachment",
        name: "nadel-banner.svg",
        contentType: "image/svg+xml",
        contentBytes: strip(NADEL_BANNER_SVG_BASE64),
        isInline: true,
        contentId: "nadelBanner",
      },
      {
        "@odata.type": "#microsoft.graph.fileAttachment",
        name: "nadel-logo.svg",
        contentType: "image/svg+xml",
        contentBytes: strip(NADEL_LOGO_SVG_BASE64),
        isInline: true,
        contentId: "nadelLogo",
      },
    ] as unknown as MicrosoftGraph.Attachment[];
  }

  /** Maps the current attachments into Microsoft Graph file attachment objects. */
  private buildGraphAttachments(): MicrosoftGraph.Attachment[] {
    return this.attachments
      .filter(attachment => !!attachment.base64)
      .map(
        attachment =>
          ({
            "@odata.type": "#microsoft.graph.fileAttachment",
            name: attachment.fileName,
            contentType: attachment.contentType ?? "application/pdf",
            contentBytes: attachment.base64,
          }) as MicrosoftGraph.Attachment
      );
  }

  /** Records the sent email as an entry in the project activity feed. */
  private recordEmailActivity(
    to: MicrosoftGraph.Recipient[],
    cc: MicrosoftGraph.Recipient[]
  ) {
    if (!this.workflowId) {
      return;
    }

    const toList = to
      .map(recipient => recipient.emailAddress?.address)
      .filter((address): address is string => !!address);
    const ccList = cc
      .map(recipient => recipient.emailAddress?.address)
      .filter((address): address is string => !!address);

    const recipientsSummary =
      ccList.length > 0
        ? `${toList.join(", ")} (CC: ${ccList.join(", ")})`
        : toList.join(", ");

    const payload: WorkflowUpdatesDto = {
      projectWorkFlowId: this.workflowId,
      message: `Proof & OC PDF email sent to ${recipientsSummary}.`,
    };

    this._projectWorkflowService.postWorkflowUpdates(payload).subscribe();
  }
}
